import { apiKeysService } from '@/api/api-keys.service';
import type { ApiKeyRuleDto, CreateApiKeyDto } from '@carvia/ros-client-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MaybeRefOrGetter } from '@vueuse/core';
import { toValue, toRef } from 'vue';

const QUERY_KEY = 'api-keys';
const GLOBAL_QUERY_KEY = 'api-keys-global';

export const useApiKeysByUserId = (
  userId: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, toRef(userId)],
    queryFn: () => apiKeysService.listByUserId(toValue(userId)),
    enabled: toRef(enabled),
  });
};

export const useGlobalApiKeys = (enabled: MaybeRefOrGetter<boolean> = true) => {
  return useQuery({
    queryKey: [GLOBAL_QUERY_KEY],
    queryFn: () => apiKeysService.listAll(),
    enabled: toRef(enabled),
  });
};

export const useCreateApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (data: CreateApiKeyDto) => apiKeysService.create(data),
    onMutate: () => ({
      successMessage: null,
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.userId] });
    },
  });
};

export const useCreateGlobalApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [GLOBAL_QUERY_KEY, 'create'],
    mutationFn: (data: CreateApiKeyDto) => apiKeysService.create(data),
    onMutate: () => ({
      successMessage: null,
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: [GLOBAL_QUERY_KEY] });
    },
  });
};

export const useUpdateApiKeyRules = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [GLOBAL_QUERY_KEY, 'update-rules'],
    mutationFn: ({ id, rules }: { id: string; rules: ApiKeyRuleDto[] }) =>
      apiKeysService.updateRules(id, { rules }),
    onMutate: () => ({
      successMessage: 'API key rules updated.',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: [GLOBAL_QUERY_KEY] });
    },
  });
};

export const useDeleteApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      apiKeysService.delete(id).then(() => ({ userId })),
    onMutate: () => ({
      successMessage: 'API key deleted.',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, result.userId] });
    },
  });
};

export const useDeleteGlobalApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [GLOBAL_QUERY_KEY, 'delete'],
    mutationFn: (id: string) => apiKeysService.delete(id),
    onMutate: () => ({
      successMessage: 'API key deleted.',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: [GLOBAL_QUERY_KEY] });
    },
  });
};
